"use client";

import type { ToolUIRegistry } from "@magnus/react";
import { lazy } from "react";

/**
 * Word tool registry. Spread after excelToolRegistry in tool-renderer.tsx so
 * these entries win. The five overlapping names (find_replace, add_comment,
 * get_comments, delete_comment, batch_operations) are explicitly registered
 * here so the Word status view handles them instead of Excel's renderer.
 */

const wordStatusView = () =>
  import("@/components/chat/word-tool-status").then(
    ({ WordToolStatusView }) => ({ default: WordToolStatusView })
  );

const step = (loader: typeof wordStatusView) => ({
  kind: "step" as const,
  category: "code" as const,
  view: lazy(loader),
});

export const wordToolRegistry = {
  // Inspection
  get_document_info: step(wordStatusView),
  get_selection_info: step(wordStatusView),
  // Read
  read_document_body: step(wordStatusView),
  read_paragraphs: step(wordStatusView),
  read_selection: step(wordStatusView),
  // Write
  insert_text: step(wordStatusView),
  insert_paragraph: step(wordStatusView),
  insert_break: step(wordStatusView),
  insert_html: step(wordStatusView),
  replace_selection: step(wordStatusView),
  delete_selection: step(wordStatusView),
  clear_selection: step(wordStatusView),
  // Formatting
  format_selection: step(wordStatusView),
  set_paragraph_alignment: step(wordStatusView),
  set_paragraph_style: step(wordStatusView),
  set_paragraph_spacing: step(wordStatusView),
  // Search
  search_document: step(wordStatusView),
  find_replace: step(wordStatusView),
  // Tables (Word-specific names — no overlap)
  insert_table: step(wordStatusView),
  get_tables: step(wordStatusView),
  // Comments (all names covered — overlapping ones win over Excel renderer)
  add_comment: step(wordStatusView),
  get_comments: step(wordStatusView),
  delete_comment: step(wordStatusView),
  reply_to_comment: step(wordStatusView),
  resolve_comment: step(wordStatusView),
  // Track Changes
  set_track_changes: step(wordStatusView),
  get_tracked_changes: step(wordStatusView),
  accept_tracked_changes: step(wordStatusView),
  reject_tracked_changes: step(wordStatusView),
  // Batch (overlapping name — Word view handles Word batches)
  batch_operations: step(wordStatusView),
  // Document
  save_document: step(wordStatusView),
} satisfies ToolUIRegistry;
